import { z } from "zod";
import type { Transaction } from "../shared/types";
import { MAX_CSV_BYTES, MAX_TRANSACTIONS, transactionSchema } from "./schemas";

function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}

export function parseCsvTransactions(text: string): Transaction[] {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) {
    throw new Error("CSV_EMPTY");
  }

  const header = parseCsvLine(lines[0]).map((h) => h.trim().toLowerCase());
  const idx = {
    booking: header.indexOf("booking_date"),
    value: header.indexOf("value_date"),
    text: header.indexOf("text"),
    amount: header.indexOf("amount"),
    source: header.indexOf("source"),
  };

  if (idx.booking < 0 || idx.value < 0 || idx.text < 0 || idx.amount < 0) {
    throw new Error("CSV_HEADERS");
  }

  const txs: Transaction[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = parseCsvLine(lines[i]);
    const amountRaw = cols[idx.amount]?.trim() ?? "";
    if (!amountRaw || !/^[+-]?(?:\d+\.?\d*|\.\d+)$/.test(amountRaw)) {
      throw new Error("CSV_INVALID");
    }
    const amount = Number(amountRaw);
    if (!Number.isFinite(amount)) {
      throw new Error("CSV_INVALID");
    }
    txs.push({
      booking_date: cols[idx.booking]?.trim() ?? "",
      value_date: cols[idx.value]?.trim() ?? "",
      text: cols[idx.text] ?? "",
      amount,
      source: idx.source >= 0 ? cols[idx.source] || "csv" : "csv",
    });
  }

  if (txs.length === 0) {
    throw new Error("CSV_EMPTY");
  }
  if (txs.length > MAX_TRANSACTIONS) {
    throw new Error("TOO_MANY_TRANSACTIONS");
  }

  const validated = z.array(transactionSchema).safeParse(txs);
  if (!validated.success) {
    throw new Error("CSV_INVALID");
  }

  return validated.data;
}

export async function importCsvFile(file: File): Promise<Transaction[]> {
  if (file.size > MAX_CSV_BYTES) {
    throw new Error("CSV_TOO_LARGE");
  }
  const text = await file.text();
  return parseCsvTransactions(text);
}
